import { Contract, parseUnits } from "ethers";
import { getActiveSigner } from "@/lib/wallet";

// Standard BEP-20 USDT transfer to the admin wallet. No custom contract — this is a
// plain token transfer the user confirms in their wallet popup.
const USDT_ABI = [
  "function transfer(address to, uint256 amount) returns (bool)",
  "function balanceOf(address) view returns (uint256)",
];

const BSC_CHAIN_HEX = "0x38"; // 56

// Make sure the connected wallet is on BNB Smart Chain before reading USDT / sending.
// A wallet sitting on Ethereum (or any other chain) makes the USDT contract call return
// empty data ("could not decode result data") or a 0 balance ("insufficient balance").
async function ensureBsc(signer) {
  const provider = signer?.provider;
  if (!provider) return signer;
  try {
    const net = await provider.getNetwork();
    if (Number(net.chainId) === 56) return signer;
  } catch (e) { /* fall through to switch */ }

  try {
    await provider.send("wallet_switchEthereumChain", [{ chainId: BSC_CHAIN_HEX }]);
  } catch (err) {
    const code = err?.code ?? err?.error?.code;
    const notAdded = code === 4902 || String(err?.message || "").includes("4902") || String(err?.message || "").toLowerCase().includes("unrecognized chain");
    if (notAdded) {
      await provider.send("wallet_addEthereumChain", [{
        chainId: BSC_CHAIN_HEX,
        chainName: "BNB Smart Chain",
        nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
        rpcUrls: ["https://bsc-dataseed.binance.org"],
        blockExplorerUrls: ["https://bscscan.com"],
      }]);
      await provider.send("wallet_switchEthereumChain", [{ chainId: BSC_CHAIN_HEX }]);
    } else {
      throw new Error("Please switch your wallet to BNB Smart Chain (BSC) and try again.");
    }
  }

  try { return await provider.getSigner(); } catch (e) { return signer; }
}

// BSC-USD (USDT on BNB Smart Chain) uses 18 decimals.
export async function depositUsdt(amountUsd, adminWallet, usdtContract) {
  let signer = await getActiveSigner();
  signer = await ensureBsc(signer);
  const usdt = new Contract(usdtContract, USDT_ABI, signer);
  const value = parseUnits(String(amountUsd), 18);
  const bal = await usdt.balanceOf(await signer.getAddress());
  if (bal < value) throw new Error("Insufficient USDT balance in your wallet");
  const tx = await usdt.transfer(adminWallet, value);
  await tx.wait();
  return tx.hash;
}
